import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { TopicCombinationService } from '../../../services/topic-combination.service';
import { WritingSessionService } from '../../../services/writing-session.service';
import { WritingHistoryService } from '../../../services/writing-history.service';
import { AuthService } from '../../../services/auth.service';
import type { TopicCombination } from '../../../models';

const MIN_YEAR = 2025;
const MIN_MONTH = 1;

@Component({
  selector: 'app-combination-view',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './combination-view.component.html',
  styleUrl: './combination-view.component.scss',
  imports: [RouterLink, DialogModule, ButtonModule]
})
export class CombinationViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly combinationService = inject(TopicCombinationService);
  private readonly session = inject(WritingSessionService);
  private readonly auth = inject(AuthService);
  private readonly writingHistory = inject(WritingHistoryService);
  private readonly destroy$ = new Subject<void>();

  protected readonly combinations = signal<TopicCombination[]>([]);
  protected readonly loading = signal(true);
  protected readonly outOfRange = signal(false);
  protected readonly toastMessage = signal<string | null>(null);
  protected readonly practiceLoading = signal(false);
  protected readonly showSubscriptionDialog = signal(false);

  private redirectTimer: ReturnType<typeof setTimeout> | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const year = Number(params.get('year'));
      const month = Number(params.get('month'));
      const combinationNumber = params.has('combinationNumber')
        ? Number(params.get('combinationNumber'))
        : undefined;

      this.loadCombination(year, month, combinationNumber);
    });
  }

  ngOnDestroy(): void {
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
      this.redirectTimer = null;
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isWithinAllowedWindow(year: number, month: number): boolean {
    if (!Number.isFinite(year) || !Number.isFinite(month)) return false;
    if (month < 1 || month > 12) return false;

    const target = year * 100 + month;
    const min = MIN_YEAR * 100 + MIN_MONTH;

    const now = new Date();
    const current = now.getFullYear() * 100 + (now.getMonth() + 1);

    return target >= min && target <= current;
  }

  private loadCombination(year: number, month: number, combinationNumber?: number): void {
    this.loading.set(true);
    this.outOfRange.set(false);
    this.combinations.set([]);

    if (!this.isWithinAllowedWindow(year, month)) {
      this.handleOutOfRange();
      return;
    }

    if (year && month) {
      this.combinationService
        .getCombinations(year, month)
        .then((list) => {
          if (!list.length) {
            this.loading.set(false);
            return;
          }

          // Already sorted descending by API; keep ability to deep-link to a specific combination if provided.
          if (combinationNumber != null) {
            const match = list.find((c) => c.combinationNumber === combinationNumber);
            this.combinations.set(match ? [match] : list);
          } else {
            this.combinations.set(list);
          }
          this.loading.set(false);
        })
        .catch(() => {
          this.loading.set(false);
        });
    } else {
      this.loading.set(false);
    }
  }

  private handleOutOfRange(): void {
    this.outOfRange.set(true);
    this.loading.set(false);
    this.redirectTimer = setTimeout(() => {
      this.router.navigate(['/writing/topics']);
    }, 2000);
  }

  protected async startPractice(combination: TopicCombination): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      this.auth.requestLogin();
      return;
    }

    this.practiceLoading.set(true);
    try {
      const eligible = await this.writingHistory.checkEligibility();
      if (eligible) {
        this.proceedToPractice(combination);
      } else {
        this.showSubscriptionDialog.set(true);
      }
    } catch {
      this.showToast('请稍后再试');
    } finally {
      this.practiceLoading.set(false);
    }
  }

  private proceedToPractice(combination: TopicCombination): void {
    this.session.saveSession(combination, ['', '', '']);
    this.router.navigate(['/writing/practice', combination.year, combination.month, combination.combinationNumber], {
      state: { selectedCombination: combination }
    });
  }

  private showToast(message: string): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastMessage.set(message);
    this.toastTimer = setTimeout(() => {
      this.toastMessage.set(null);
    }, 3500);
  }

  protected closeSubscriptionDialog(): void {
    this.showSubscriptionDialog.set(false);
  }

  protected onSubscriptionDialogOk(): void {
    this.showSubscriptionDialog.set(false);
    this.router.navigate(['/'], { fragment: 'pricing-title' }).then(() => {
      setTimeout(() => {
        document.getElementById('pricing-title')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });
  }
}
